<template lang="pug">
  div
    TableWrapper(
      loading-text="Loading... Please wait",
      ref="table",
      type="log",
      v-bind="$attrs",
      :items="formattedLogs",
      :items-per-page="10000",
      :loading="isLoading",
      :misc="{}",
      @sort="fetch(true)",
    )
      template(v-slot:default="{ item, index, headers, misc }")
        tr.no-hover(v-if="index === 0 && skipPrev > 0 && items.length !== count")
          td.text-center(colspan="3")
            v-btn(
              small,
              color="primary",
              :ripple="false",
              :loading="isLoading",
              @click="fetch('prev')",
            ) Fetch previous
        tr(:class="{'active-row': item && params && item.id === params.id }")
          td
            span {{ item.date }}
          td
            span {{ item.shop }}
          td
            div
              component(:is="item.message")
        tr.no-hover(v-if="index + 1 === formattedLogs.length && skipPrev > 0 && items.length !== count")
          td.text-center(colspan="3")
            v-btn(
              small,
              color="primary",
              :ripple="false",
              :loading="isLoading",
              @click="fetch('next')",
            ) Load more

</template>

<script>
import { map, mergeRight, pick, pipe, prop } from 'ramda';
import { progressWrapper } from '@/config/mixins.js';
import { renderLog } from '@/utils/renderer';
import { endOfDay, formatDate, startOfDay } from '@/utils/time';

import TableWrapper from '@/components/wrapper/TableWrapper.vue';

export default {
  props: {
    params: {
      type: Object,
      default: () => {},
    },

    dates: {
      type: Array,
      default: () => [],
    },

    shopId: {
      type: String,
      default: null,
    },

    limit: {
      type: Number,
      default: 10,
    },
  },

  components: {
    TableWrapper,
  },

  mixins: [progressWrapper],

  data: () => ({
    items: [],
    count: 0,
    skipPrev: 0,
  }),

  computed: {
    formattedLogs: pipe(
      prop('items'),
      map(item =>
        mergeRight(item.event, {
          id: item._id,
          shop: item.shop_name || 'Superadmin',
          message: renderLog(item),
          date: formatDate(item.date, 'dd-MM-yyyy H:mm:ss'),
        }),
      ),
    ),
  },

  methods: {
    fetch(type) {
      const defaultFetchType = {
        limit: this.limit,
        skip: undefined,
      };

      // The initial load already jumps to the newest entries (skip = count -
      // limit), so there is never a "more recent" page for `next` to fetch -
      // asking for `items.length + skipPrev` always lands past the end of
      // the collection and silently returns nothing (the "Load more" button
      // that never responded). Both directions actually need to walk further
      // into the past, same as `prev`, just triggered from a second button.
      const olderPage = {
        limit: this.skipPrev < this.limit ? this.skipPrev : this.limit,
        skip: Math.max(this.skipPrev - this.limit, 0),
      };

      const fetchTypes = {
        prev: olderPage,
        next: olderPage,
      };

      const payload = mergeRight(
        fetchTypes[type] || defaultFetchType,
        this.params,
      );

      if (Array.isArray(this.dates) && this.dates[0] && this.dates[1]) {
        const [startDate, endDate] = this.dates;

        payload.start_date = startOfDay(startDate).toISOString();
        payload.end_date = endOfDay(endDate).toISOString();
      }

      if (this.shopId) {
        payload.shop_id = this.shopId;
      }

      return this.wrapProgress(() => {
        this.$store.dispatch('log/fetch', payload).then(res => {
          if (type === true) {
            this.count = res.count;
            this.skipPrev = res.skip;
            this.items = res.data;
          } else if (type === 'prev' || type === 'next') {
            this.skipPrev = res.skip;
            this.items = res.data.concat(this.items);
          }
        });
      }).catch(error => {
        this.$router.push({ name: 'dashboard' });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.active-row {
  background-color: #e1f5fe;
  &:hover {
    background-color: #e1f5fe !important;
  }
}
</style>
